import { baseApi } from "./baseApi";
import type { NegotiationRequest } from "../../components/NegotiationRequest";

export interface RelocationOrder {
  id: string;
  order_number: string | null;
  order_type: "local_relocation" | "interstate_relocation";
  price: string;
  status:
    | "pending"
    | "accepted"
    | "rejected"
    | "counter_sent"
    | "customer_countered"
    | "customer_accepted"
    | "delivered"
    | "picked_up";
  payment_status: "paid" | "unpaid";
  payment_preference: "before_delivery" | "after_delivery";
  // "cash" or "wallet". Optional here since not yet confirmed for relocation
  // orders specifically — the badge just doesn't render if these are absent.
  payment_method?: string;
  cash_collected?: boolean | null;
  cash_collected_at?: string | null;
  customer_confirmed_cash_at?: string | null;
  created_at: string;
  pickup_location: string | null;
  destination: string | null;
  pickup_date: string | null;
  pickup_date_type: "today" | "scheduled" | null;
  pickup_time: string | null;
  latitude: string | number | null;
  longitude: string | number | null;
  dest_lat: string | number | null;
  dest_lng: string | number | null;
  move_type: string | null;
  move_size: string | null;
  vehicle_type: string | null;
  has_loading_unloading: boolean | null;
  has_packaging_materials: boolean | null;
  extra_stops: string[] | null;
  inventory_description: string | null;
  delivery_note: string | null;
  package_type: string | null;
  package_weight: string | number | null;
  package_size: string | null;
  contact_name: string | null;
  contact_phone: string | null;
  recipient_name: string | null;
  recipient_phone: string | null;
  recipient_additional_info: string | null;
  route: {
    id: string;
    from: string;
    to: string;
    price: number;
  } | null;
  customer: {
    id: string;
    name: string;
    phone: string;
    email: string;
  } | null;
  counter_offer: {
    company_offered_price: string | null;
    customer_offered_price: string | null;
    description: string;
    proposed_by: "company" | "customer";
    created_at: string;
  } | null;
  // Set when the customer tapped "Negotiate"; null otherwise.
  negotiation_request?: NegotiationRequest | null;
  // Only if the API adds it: the price before negotiation, after `price` becomes the agreed one.
  original_price?: string | null;
}

export interface RelocationOrdersResponse {
  status: string;
  data: {
    counts: {
      total: number;
      pending: number;
      counter_sent: number;
      customer_countered: number;
      accepted: number;
      rejected: number;
    };
    data: RelocationOrder[];
    meta: {
      current_page: number;
      last_page: number;
      per_page: number;
      total: number;
    };
  };
}

export interface GetRelocationOrderResponse {
  status: string;
  data: {
    counts: {
      total: number;
      pending: number;
      counter_sent: number;
      accepted: number;
      rejected: number;
    };
    data: RelocationOrder;
  };
}

export interface RelocationOrdersParams {
  order_type?: "local_relocation" | "interstate_relocation";
  per_page?: number;
  page?: number;
  search?: string;
  sort?: "asc" | "desc";
}

const relocationApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getRelocationOrderById: builder.query<GetRelocationOrderResponse, string>({
      query: (id) => ({ url: `/relocation-orders/${id}` }),
      providesTags: (_result, _err, id) => [{ type: "RelocationOrders", id }],
    }),

    getRelocationOrders: builder.query<
      RelocationOrdersResponse,
      RelocationOrdersParams | void
    >({
      query: (params) => ({
        url: "/relocation-orders",
        params: params || {},
      }),
      providesTags: ["RelocationOrders"],
    }),

    acceptRelocationOrder: builder.mutation<{ message: string }, { id: string }>(
      {
        query: ({ id }) => ({
          url: `/relocation-orders/${id}/accept`,
          method: "POST",
        }),
        invalidatesTags: ["RelocationOrders"],
      }
    ),

    rejectRelocationOrder: builder.mutation<{ message: string }, { id: string }>(
      {
        query: ({ id }) => ({
          url: `/relocation-orders/${id}/reject`,
          method: "POST",
        }),
        invalidatesTags: ["RelocationOrders"],
      }
    ),

    counterRelocationOrder: builder.mutation<
      { message: string },
      { id: string; offered_price: number; description: string }
    >({
      query: ({ id, ...body }) => ({
        url: `/relocation-orders/${id}/counter`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["RelocationOrders"],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetRelocationOrderByIdQuery,
  useGetRelocationOrdersQuery,
  useAcceptRelocationOrderMutation,
  useRejectRelocationOrderMutation,
  useCounterRelocationOrderMutation,
} = relocationApi;
